import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, AlertCircle } from "lucide-react";

const PRESETS = [
  { minutes: 15, label: "15 min" },
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hour" },
];

/**
 * LockScreenFocusModal - "Keep this on my Lock Screen"
 * Handles:
 *  - Sign-in gate for anonymous users
 *  - Replace-confirmation if another task is already the focus
 *  - Interval selection (15 / 30 / 60 / custom)
 *  - Push permission + subscription flow
 */
export const LockScreenFocusModal = ({
  open,
  onClose,
  taskId,
  taskName,
  currentFocus, // { taskId, taskName, intervalMinutes } | null
  isAuthenticated,
  onSignIn,
  onEnable,          // async ({ intervalMinutes, pushSubscription }) => { ok, error? }
  subscribeToPush,   // async () => subscription | null
  permissionStatus,  // "granted" | "denied" | "default"
  isIOSSafariNoPWA,  // boolean - user must install PWA first
  isSupported,       // boolean
}) => {
  const [interval, setIntervalMin] = useState(30);
  const [customMin, setCustomMin] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isReplace = currentFocus && currentFocus.taskId && currentFocus.taskId !== taskId;

  const chosenInterval = showCustom ? Math.max(1, Math.min(1440, parseInt(customMin, 10) || 0)) : interval;

  const handleConfirm = async () => {
    setError(null);
    if (chosenInterval < 1) {
      setError("Pick an interval of at least 1 minute.");
      return;
    }
    setSubmitting(true);
    try {
      // Ensure push permission + subscription
      let subscription = null;
      if (subscribeToPush) {
        subscription = await subscribeToPush();
        if (!subscription) {
          // On iOS Safari (non-PWA) push subscribe will never succeed. Show a
          // calmer, actionable install hint instead of a red permission error.
          if (isIOSSafariNoPWA) {
            setError("Add this app to your Home Screen (Share → Add to Home Screen) and open it from there — iOS only allows Lock Screen nudges from installed apps.");
          } else if (permissionStatus === "denied") {
            setError("Notifications are blocked in your browser settings. Turn them on for this site, then try again.");
          } else {
            setError("Notifications weren't enabled — try again and choose Allow when prompted.");
          }
          setSubmitting(false);
          return;
        }
      }
      const result = await onEnable({ intervalMinutes: chosenInterval, pushSubscription: subscription });
      if (result.ok) {
        onClose();
      } else {
        setError(result.error || "Something went sideways. Give it another try.");
      }
    } catch (err) {
      setError(err?.message || "Something went sideways.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80]"
            data-testid="lockscreen-modal-backdrop"
          />
          {/* Modal - centered via framer-motion (Tailwind translate classes get overridden by motion's transform) */}
          <motion.div
            initial={{ opacity: 0, x: "-50%", y: "calc(-50% + 8px)", scale: 0.98 }}
            animate={{ opacity: 1, x: "-50%", y: "-50%", scale: 1 }}
            exit={{ opacity: 0, x: "-50%", y: "calc(-50% + 6px)", scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed left-1/2 top-1/2 w-[92%] max-w-md max-h-[90vh] overflow-y-auto bg-[#FDFBF7] rounded-3xl shadow-2xl z-[81]"
            data-testid="lockscreen-modal"
            role="dialog"
            aria-labelledby="lockscreen-modal-title"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#E0F7FA] flex items-center justify-center">
                  <Lock size={18} className="text-[#00838F]" />
                </div>
                <div>
                  <h2 id="lockscreen-modal-title" className="text-lg font-semibold text-[#27272A]">
                    Keep this on my Lock Screen
                  </h2>
                  <p className="text-xs text-[#71717A] mt-0.5">A gentle nudge back, on your own schedule.</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-full text-[#A1A1AA] hover:text-[#52525B] hover:bg-stone-100 transition-colors"
                aria-label="Close"
                data-testid="lockscreen-modal-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 pb-6 space-y-5">
              {/* Anonymous gate */}
              {!isAuthenticated && (
                <div className="bg-[#FEF6E7] border border-[#F5D680] rounded-2xl p-4 text-sm text-[#78450F]">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium mb-1">Sign in to enable Lock Screen Nudges</p>
                      <p className="text-[#8A5B15] leading-relaxed">
                        Signing in keeps your focus task synced across your phone and any browser you open the app in.
                      </p>
                      <button
                        onClick={onSignIn}
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0CBCBF] hover:bg-[#0A9A9D] text-white text-sm font-medium transition-colors"
                        data-testid="lockscreen-signin-btn"
                      >
                        Sign in
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* iOS PWA install hint — this is the whole story on iOS
                  Safari (non-PWA). We hide the interval picker so the user
                  doesn't set something up that we can't actually deliver. */}
              {isAuthenticated && isIOSSafariNoPWA && (
                <>
                  <div className="bg-[#FEF6E7] border border-[#F5D680] rounded-2xl p-4 text-sm text-[#78450F]">
                    <p className="font-medium mb-1">Add this app to your Home Screen first</p>
                    <p className="text-[#8A5B15] leading-relaxed">
                      iOS only lets us send Lock Screen nudges from an installed app. In Safari, tap the Share button below, then <span className="font-medium">Add to Home Screen</span>. Open the app from your Home Screen and come back here — your task will be ready.
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-full px-4 py-2.5 rounded-full text-sm font-medium bg-[#0CBCBF] hover:bg-[#0A9A9D] text-white transition-colors"
                    data-testid="lockscreen-ios-dismiss"
                  >
                    Got it
                  </button>
                </>
              )}

              {/* Unsupported browser (not iOS) */}
              {isAuthenticated && !isIOSSafariNoPWA && !isSupported && (
                <div className="bg-stone-100 border border-stone-200 rounded-2xl p-4 text-sm text-[#52525B]">
                  Your current browser doesn't support Lock Screen notifications. Try Chrome, Edge, Firefox, or the installed app.
                </div>
              )}

              {/* Task summary */}
              {isAuthenticated && isSupported && (
                <>
                  <div className="rounded-2xl bg-white border border-stone-100 p-4 shadow-sm">
                    <p className="text-[11px] uppercase tracking-wider text-[#A1A1AA] mb-1.5">Your focus task</p>
                    <p className="text-[15px] text-[#27272A] font-medium leading-snug break-words" data-testid="lockscreen-selected-task">
                      {taskName}
                    </p>
                  </div>

                  {/* Replace warning */}
                  {isReplace && (
                    <div className="bg-[#F6FAFA] border border-[#B8E6E7] rounded-2xl p-3.5 text-sm text-[#00838F]">
                      <p className="leading-relaxed">
                        This will replace your current focus: <span className="font-medium">"{currentFocus.taskName}"</span>. Only one task at a time — that's the point.
                      </p>
                    </div>
                  )}

                  {/* Interval picker */}
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-[#A1A1AA] mb-2">Reminder every</p>
                    <div className="grid grid-cols-4 gap-2">
                      {PRESETS.map((preset) => (
                        <button
                          key={preset.minutes}
                          onClick={() => { setIntervalMin(preset.minutes); setShowCustom(false); }}
                          className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                            !showCustom && interval === preset.minutes
                              ? "bg-[#0CBCBF] text-white shadow-sm"
                              : "bg-white border border-stone-200 text-[#52525B] hover:border-[#B8E6E7]"
                          }`}
                          data-testid={`lockscreen-interval-${preset.minutes}`}
                        >
                          {preset.label}
                        </button>
                      ))}
                      <button
                        onClick={() => setShowCustom(true)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          showCustom
                            ? "bg-[#0CBCBF] text-white shadow-sm"
                            : "bg-white border border-stone-200 text-[#52525B] hover:border-[#B8E6E7]"
                        }`}
                        data-testid="lockscreen-interval-custom"
                      >
                        Custom
                      </button>
                    </div>
                    {showCustom && (
                      <div className="mt-3 flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="1440"
                          value={customMin}
                          onChange={(e) => setCustomMin(e.target.value)}
                          placeholder="Minutes"
                          className="flex-1 px-3 py-2 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-[#0CBCBF]"
                          data-testid="lockscreen-interval-custom-input"
                        />
                        <span className="text-sm text-[#71717A]">min</span>
                      </div>
                    )}
                  </div>

                  {/* Permission status hint */}
                  {permissionStatus === "denied" && (
                    <p className="text-xs text-[#B84A2E]">
                      Notifications are blocked in your browser. Enable them in Settings, then come back here.
                    </p>
                  )}

                  {error && (
                    <div
                      className="bg-[#FEF6E7] border border-[#F5D680] rounded-xl p-3 text-sm text-[#78450F] leading-relaxed"
                      data-testid="lockscreen-error"
                    >
                      {error}
                    </div>
                  )}

                  <p className="text-[11px] text-[#A1A1AA] leading-relaxed">
                    iOS controls when notifications actually appear on your Lock Screen — we can't promise a permanent spot, but we'll send a gentle ping at your chosen interval.
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={onClose}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-[#52525B] hover:bg-stone-100 transition-colors"
                      data-testid="lockscreen-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirm}
                      disabled={submitting || permissionStatus === "denied"}
                      className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium bg-[#0CBCBF] hover:bg-[#0A9A9D] text-white disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors"
                      data-testid="lockscreen-confirm-btn"
                    >
                      {submitting ? "Setting up…" : (isReplace ? "Replace focus" : "Keep on Lock Screen")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default LockScreenFocusModal;
